/**
 * The Speedy player (F-129): mounted once in Layout around the page. `useSpeedyPlayer().open()`
 * shows the full-screen overlay over whatever page is open; the page stays mounted underneath.
 * With the `speedies` flag off this renders its children and nothing else.
 *
 * Sessions: `player_opened` on open; `player_session_ended` once per session, on close, or with
 * keepalive when the tab is hidden (which ends that session; coming back starts a new one).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { isSpeediesEnabled } from '@/lib/feature-flags';
import { api, primeKeepaliveAuth } from '@/api/client';
import { useTrackEntityAction } from '@/hooks/use-entity-tracker';
import { invalidateSpeedyReads, useSpeedyCards, useSpeedyFeed } from '@/hooks/use-speedies';
import { useQueryClient } from '@tanstack/react-query';
import { getNavSessionId } from '@/lib/tracking/nav-tracker';
import { speedyActivityRow, type SpeedyEvent, type SpeedyExit } from '@/lib/tracking/speedy-analytics';
import { pauseAll } from '@/lib/media/playback-coordinator';
import type { SpeedyCard } from '@/api/speedies';
import { createOverlayController, type OpenPlayerArgs, type OverlayController } from './player-store';
import { SpeedyReel, type ReelCounters } from './SpeedyReel';
import type { DetailsTarget } from './SpeedyDetailsPanel';

interface SpeedyPlayerContextValue {
  open: (args: OpenPlayerArgs) => void;
  isOpen: boolean;
}

const SpeedyPlayerContext = createContext<SpeedyPlayerContextValue>({ open: () => {}, isOpen: false });

export function useSpeedyPlayer(): SpeedyPlayerContextValue {
  return useContext(SpeedyPlayerContext);
}

export function SpeedyPlayerProvider({ children }: { children: React.ReactNode }) {
  if (!isSpeediesEnabled()) return <>{children}</>;
  return <ActiveProvider>{children}</ActiveProvider>;
}

/** Sessions that write no player analytics: an admin reviewing, or the member's own upload. */
function isOwnClipSession(a: OpenPlayerArgs): boolean {
  if (a.untracked) return true;
  return a.source.kind === 'given' && a.source.items.length > 0 && a.source.items.every((c) => !!c.local_clip_url);
}

function newCounters(): ReelCounters & { openedAt: number } {
  return { viewed: new Set(), completed: new Set(), skips: 0, openedAt: Date.now() };
}

function ActiveProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const navigate = useNavigate();
  const track = useTrackEntityAction();
  const queryClient = useQueryClient();
  const [args, setArgs] = useState<OpenPlayerArgs | null>(null);
  const counters = useRef(newCounters());
  const sessionWritten = useRef(false);
  // A Speedy finished at any point while open (the session counters reset after the tab is hidden).
  const finishedAny = useRef(false);
  const argsRef = useRef<OpenPlayerArgs | null>(null);
  argsRef.current = args;

  const controller = useMemo<OverlayController>(() => createOverlayController({
    state: () => router.history.location.state as unknown as Record<string, unknown> | undefined,
    push: (state) => router.history.push(router.history.location.href, state as never),
    replace: (state) => router.history.replace(router.history.location.href, state as never),
    back: () => router.history.back(),
    subscribe: (fn) => router.history.subscribe(fn),
  }), [router]);

  // A reload with our entry still in history state never reopens the player.
  useEffect(() => controller.clearStale(), [controller]);

  const emit = useCallback((event: SpeedyEvent, opts?: { keepalive?: boolean }) => {
    const row = speedyActivityRow(event, getNavSessionId());
    if (opts?.keepalive) {
      void api.logActivity({
        type: 'speedy',
        action: row.action,
        entity_type: row.entityType,
        entity_id: row.entityId,
        entity_name: row.entityName,
        description: `${row.action} speedy`,
        metadata: row.metadata,
      } as never, { keepalive: true });
    } else {
      track(row.action, row.entityType, row.entityId, row.entityName, row.metadata);
    }
  }, [track]);

  const writeSessionEnd = useCallback((exit: SpeedyExit, keepalive = false) => {
    const a = argsRef.current;
    if (!a || sessionWritten.current || isOwnClipSession(a)) return;
    sessionWritten.current = true;
    const c = counters.current;
    emit({
      action: 'player_session_ended',
      entry: a.entry,
      durationMs: Date.now() - c.openedAt,
      itemsViewed: c.viewed.size,
      itemsCompleted: c.completed.size,
      skips: c.skips,
      exit,
    }, { keepalive });
  }, [emit]);

  const open = useCallback((a: OpenPlayerArgs) => {
    const accepted = controller.open((exit) => {
      writeSessionEnd(exit);
      // Finished Speedies change "finished", series progress and Saved. Refreshed on close, not
      // during the session, so the reel's own list never reorders under the viewer.
      if (finishedAny.current || counters.current.completed.size > 0) invalidateSpeedyReads(queryClient, 'completion');
      setArgs(null);
    });
    if (!accepted) return;
    primeKeepaliveAuth();
    pauseAll();
    counters.current = newCounters();
    sessionWritten.current = false;
    finishedAny.current = false;
    setArgs(a);
    // The member watching their own upload isn't a viewing session.
    if (isOwnClipSession(a)) return;
    const startId = a.startSpeedyId ?? (a.source.kind === 'single' ? a.source.speedyId : null);
    emit({
      action: 'player_opened',
      entry: a.entry,
      speedyId: startId,
      slot: a.slot,
      tileIndex: a.tileIndex,
      communityId: a.communityId,
      channelId: a.channelId,
    });
  }, [controller, emit, writeSessionEnd, queryClient]);

  // Hidden ends the session (keepalive); visible again starts a new one.
  useEffect(() => {
    if (!args) return;
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        writeSessionEnd('hidden', true);
      } else if (sessionWritten.current) {
        // A new session starts. Reset in place: the reel holds this same object.
        if (counters.current.completed.size > 0) finishedAny.current = true;
        Object.assign(counters.current, newCounters());
        sessionWritten.current = false;
      }
    };
    const onHide = () => writeSessionEnd('hidden', true);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', onHide);
    };
  }, [args, writeSessionEnd]);

  const value = useMemo(() => ({ open, isOpen: !!args }), [open, args]);

  const go = useCallback((target: DetailsTarget) => {
    // Captured now: by the time the close lands the current args are gone.
    const entry = argsRef.current?.entry ?? 'post_attachment';
    controller.closeThen(() => {
      if (target.kind === 'course') {
        void navigate({ to: '/learn/courses/$courseId', params: { courseId: target.courseId } });
      } else if (target.kind === 'lesson') {
        void navigate({
          to: '/learn/courses/$courseId/lessons/$lessonId',
          params: { courseId: target.courseId, lessonId: target.lessonId },
        });
      } else {
        // Another Speedy: open it once this one has closed.
        setTimeout(() => open({ entry, source: { kind: 'single', speedyId: target.speedyId } }), 0);
      }
    });
  }, [controller, navigate, open]);

  return (
    <SpeedyPlayerContext.Provider value={value}>
      {children}
      {args && (
        <SpeedyOverlay
          args={args}
          counters={counters.current}
          onRequestClose={(exit) => controller.requestClose(exit)}
          onGo={go}
        />
      )}
    </SpeedyPlayerContext.Provider>
  );
}

function SpeedyOverlay({
  args,
  counters,
  onRequestClose,
  onGo,
}: {
  args: OpenPlayerArgs;
  counters: ReelCounters;
  onRequestClose: (exit: SpeedyExit) => void;
  onGo: (t: DetailsTarget) => void;
}) {
  const detailsOpenRef = useRef<(() => void) | null>(null);
  return (
    <DialogPrimitive.Root open onOpenChange={(o) => { if (!o) onRequestClose('close'); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          className="fixed inset-0 z-[100] bg-black outline-none"
          aria-describedby={undefined}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            const closeDetails = detailsOpenRef.current;
            if (closeDetails) closeDetails();
            else onRequestClose('escape');
          }}
        >
          <DialogPrimitive.Title className="sr-only">Speedy player</DialogPrimitive.Title>
          <ReelSource args={args} counters={counters} onClose={() => onRequestClose('close')} onGo={onGo} detailsOpenRef={detailsOpenRef} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Resolves the list for the reel: the feed (paged), one Speedy by id, or a list given by the caller. */
function ReelSource({
  args,
  counters,
  onClose,
  onGo,
  detailsOpenRef,
}: {
  args: OpenPlayerArgs;
  counters: ReelCounters;
  onClose: () => void;
  onGo: (t: DetailsTarget) => void;
  detailsOpenRef: React.MutableRefObject<(() => void) | null>;
}) {
  const src = args.source;
  const feed = useSpeedyFeed(src.kind === 'open_feed');
  const single = useSpeedyCards(src.kind === 'single' ? [src.speedyId] : []);

  let items: SpeedyCard[] = [];
  let loading = false;
  let failed = false;
  if (src.kind === 'given') items = src.items;
  else if (src.kind === 'single') {
    items = single.data ?? [];
    loading = single.isPending && !single.isError;
    failed = single.isError;
  } else {
    items = feed.data?.pages.flat() ?? [];
    loading = feed.isLoading;
    // A failed "load more" keeps the reel playing what it has.
    failed = feed.isError && items.length === 0;
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-white" role="status">
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
        <span className="sr-only">Loading Speedies</span>
      </div>
    );
  }
  if (failed || items.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center text-white">
        <p>{failed ? "Couldn't load Speedies." : "This Speedy isn't available."}</p>
        <div className="flex gap-2">
          {failed && (
            <button
              type="button"
              onClick={() => void (src.kind === 'single' ? single.refetch() : feed.refetch())}
              className="min-h-[44px] rounded-full border border-white/40 px-4 text-sm font-semibold"
            >
              Try again
            </button>
          )}
          <button type="button" onClick={onClose} className="min-h-[44px] rounded-full bg-white px-4 text-sm font-semibold text-black">Close</button>
        </div>
      </div>
    );
  }

  const startIndex = args.startSpeedyId ? Math.max(0, items.findIndex((c) => c.id === args.startSpeedyId)) : 0;
  return (
    <SpeedyReel
      items={items}
      startIndex={startIndex}
      hasMore={src.kind === 'open_feed' && !!feed.hasNextPage}
      loadMore={() => void feed.fetchNextPage()}
      loadingMore={feed.isFetchingNextPage}
      loadMoreFailed={src.kind === 'open_feed' && feed.isFetchNextPageError}
      counters={counters}
      celebrate={args.celebrate}
      onClose={onClose}
      onGo={onGo}
      detailsOpenRef={detailsOpenRef}
    />
  );
}
